import { ReactNode, useEffect, useRef } from 'react';
import { cn } from '../../../utils/cn';
import { Button } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { Portal } from '../Portal/Portal';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl';
const SIZES: Record<ModalSize, string> = { sm: 'sm:max-w-md', md: 'sm:max-w-xl', lg: 'sm:max-w-3xl', xl: 'sm:max-w-5xl' };
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Dialog. On phones it opens as a bottom sheet (easy to reach with a thumb). */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  dismissible?: boolean;
}

export function Modal({ open, onClose, title, description, children, footer, size = 'md', dismissible = true }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const first = panelRef.current?.querySelector<HTMLElement>('[data-autofocus]') || panelRef.current;
    setTimeout(() => first?.focus(), 0);

    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && dismissible) {
        e.stopPropagation();
        onClose?.();
      }
      if (e.key === 'Tab' && panelRef.current) {
        const els = [...panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
        if (!els.length) return;
        const firstEl = els[0];
        const lastEl = els[els.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.();
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
        <div className="absolute inset-0 bg-navy-900/50 animate-fade" onClick={() => dismissible && onClose?.()} aria-hidden="true" />
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          tabIndex={-1}
          className={cn(
            'relative flex max-h-[92vh] w-full flex-col bg-white shadow-pop outline-none',
            'rounded-t-3xl animate-sheet sm:rounded-2xl sm:animate-pop',
            SIZES[size],
          )}
        >
          <div className="mx-auto mt-2 h-1.5 w-10 rounded-full bg-navy-100 sm:hidden" aria-hidden="true" />
          <div className="flex items-start gap-3 px-5 pb-3 pt-4 sm:pt-5">
            <div className="min-w-0 flex-1">
              <h2 id="modal-title" className="text-lg font-semibold">{title}</h2>
              {description && <p className="mt-0.5 text-sm text-ink-500">{description}</p>}
            </div>
            {dismissible && <IconButton icon="x" label="Close" size="sm" onClick={onClose} className="-mr-1" />}
          </div>
          <div className="overflow-y-auto px-5 pb-5">{children}</div>
          {footer && (
            <div className="pb-safe-3 flex flex-col-reverse gap-2 border-t border-line px-5 pt-3 sm:flex-row sm:justify-end">{footer}</div>
          )}
        </div>
      </div>
    </Portal>
  );
}

/** Yes/no question before a risky action (delete, cancel invoice...). */
export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
  loading?: boolean;
}

export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', cancelLabel, danger, loading }: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {cancelLabel ?? (danger ? 'Keep it' : 'Cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-ink-600">{message}</p>
    </Modal>
  );
}

export default Modal;
